import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Zap, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Signup() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "", city: "" });
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      setUser(data.user);
      toast.success(`Welcome to RushGro, ${data.user.name}!`);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="absolute inset-0 -z-10 opacity-40"
           style={{ backgroundImage: "radial-gradient(circle at 25% 20%, hsl(217 91% 60% / 0.18), transparent 45%), radial-gradient(circle at 80% 60%, hsl(160 90% 50% / 0.12), transparent 40%)" }} />
      <div className="absolute inset-0 -z-10"
           style={{ backgroundImage: "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.2 }} />

      <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col min-h-screen">
        <Link to="/" className="flex items-center gap-2 w-fit" data-testid="signup-logo-link">
          <div className="w-8 h-8 rounded-lg bg-blue-600 grid place-items-center">
            <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-heading text-lg font-semibold tracking-tight">RushGro</div>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1">PBaaS</span>
        </Link>

        <div className="grid md:grid-cols-2 gap-10 flex-1 items-center mt-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-500 font-medium">Get started · Free trial</div>
            <h1 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-semibold tracking-tight">
              Power every outlet <span className="rushgro-gradient-text">without buying batteries.</span>
            </h1>
            <p className="mt-4 text-muted-foreground text-sm max-w-md">
              Create your brand workspace in under a minute. Add outlets, request FlexiPod installations, monitor live health and pay one flat monthly subscription.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Onboarding call within 24 hours",
                "Zero capex — replace batteries with a subscription",
                "Live IoT telemetry from every outlet",
                "One dashboard for brand, outlet, service and finance",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {t}
                </li>
              ))}
            </ul>
          </div>

          <Card className="p-6 md:p-8 rounded-xl border">
            <h2 className="font-heading text-xl font-semibold">Create your workspace</h2>
            <p className="text-sm text-muted-foreground mt-1">Already have an account? <Link to="/login" className="text-blue-500 hover:underline" data-testid="signup-signin-link">Sign in</Link></p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Full name</Label><Input required data-testid="signup-name-input" value={form.name} onChange={set("name")} /></div>
                <div className="space-y-1"><Label>Company</Label><Input required data-testid="signup-company-input" value={form.company} onChange={set("company")} /></div>
              </div>
              <div className="space-y-1"><Label>Work email</Label><Input required type="email" data-testid="signup-email-input" value={form.email} onChange={set("email")} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Phone</Label><Input data-testid="signup-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91…" /></div>
                <div className="space-y-1"><Label>City</Label><Input data-testid="signup-city-input" value={form.city} onChange={set("city")} /></div>
              </div>
              <div className="space-y-1"><Label>Password</Label><Input required type="password" minLength={8} data-testid="signup-password-input" value={form.password} onChange={set("password")} placeholder="min. 8 characters" /></div>
              {err && <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="signup-error">{err}</div>}
              <Button type="submit" disabled={loading} data-testid="signup-submit-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <>Create workspace <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">By signing up you agree to the RushGro terms & privacy policy.</p>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
